// 10 trading strategies — bilingual EN/AR data
import {
  TrendingUp,
  Hammer,
  GitBranch,
  Activity,
  Waves,
  BarChart3,
  Layers,
  Crosshair,
  Flag,
  AlertTriangle,
} from "lucide-react";

export const STRATEGIES = [
  {
    id: "engulfing",
    icon: TrendingUp,
    color: "from-emerald-400 to-green-500",
    name: { en: "Engulfing Patterns", ar: "نماذج الابتلاع" },
    summary: {
      en: "Bullish & Bearish reversal — one of the most reliable signals",
      ar: "نموذج انعكاسي صاعد وهابط — من أقوى الإشارات",
    },
    whenToUse: {
      en: [
        "At major support / resistance levels or supply/demand zones",
        "After a prolonged trend (5+ candles in one direction)",
        "Volume should be higher on the engulfing candle for confirmation",
      ],
      ar: [
        "عند مستويات الدعم/المقاومة الرئيسية أو مناطق العرض/الطلب",
        "بعد اتجاه ممتد (5 شموع أو أكثر باتجاه واحد)",
        "يفضل أن يكون حجم التداول أعلى على شمعة الابتلاع للتأكيد",
      ],
    },
    markets: {
      en: ["Forex: EUR/USD, GBP/USD, USD/JPY", "Stocks: AAPL, TSLA (earnings)", "Crypto: BTC/USD, ETH/USD (daily)", "Commodities: Gold (XAU/USD), Oil (WTI)"],
      ar: ["الفوركس: EUR/USD, GBP/USD, USD/JPY", "الأسهم: AAPL, TSLA خلال موسم الأرباح", "الكريبتو: BTC/USD, ETH/USD على الديلي", "السلع: الذهب XAU/USD والنفط WTI"],
    },
    entry: {
      en: [
        "Bullish Engulfing: small red candle followed by a larger green candle that fully covers it (bottom of downtrend)",
        "Bearish Engulfing: small green candle followed by a larger red candle (top of uptrend)",
        "Wait for the engulfing candle to close before entering",
      ],
      ar: [
        "ابتلاع صاعد: شمعة حمراء صغيرة تليها شمعة خضراء أكبر تغطيها بالكامل (قاع اتجاه هابط)",
        "ابتلاع هابط: شمعة خضراء صغيرة تليها شمعة حمراء أكبر (قمة اتجاه صاعد)",
        "انتظر إغلاق شمعة الابتلاع قبل الدخول",
      ],
    },
    exit: {
      en: ["Stop loss below the engulfing pattern (bullish) or above (bearish)", "Take profit at next key support / resistance"],
      ar: ["وقف الخسارة أسفل نموذج الابتلاع (الصاعد) أو أعلاه (الهابط)", "جني الأرباح عند أقرب دعم/مقاومة رئيسي"],
    },
    risk: { en: "Moderate to High", ar: "متوسطة إلى مرتفعة" },
    timeframes: [
      { tf: "1H", confidence: "60%", stars: 3, rr: "1:1.5" },
      { tf: "4H", confidence: "72%", stars: 4, rr: "1:2" },
      { tf: "Daily", confidence: "80%", stars: 5, rr: "1:3" },
    ],
    pros: {
      en: ["One of the most reliable reversal signals", "Easy to identify visually", "Works on all markets"],
      ar: ["من أكثر إشارات الانعكاس موثوقية", "سهل التعرف عليه بصرياً", "يعمل على جميع الأسواق"],
    },
    cons: {
      en: ["Less reliable on lower timeframes (noise)", "Needs confirmation from volume or S/R"],
      ar: ["أقل موثوقية على الفريمات الصغيرة (ضوضاء)", "يحتاج تأكيد من الحجم أو الدعم/المقاومة"],
    },
  },
  {
    id: "hammer-shooting-star",
    icon: Hammer,
    color: "from-orange-400 to-amber-500",
    name: { en: "Hammer & Shooting Star", ar: "المطرقة والنجم الساقط" },
    summary: { en: "Single-candle reversal patterns at key levels", ar: "نماذج انعكاس بشمعة واحدة عند المستويات الرئيسية" },
    whenToUse: {
      en: [
        "At strong support (Hammer) or resistance (Shooting Star) zones",
        "After a minimum of 3 trend candles in one direction",
        "Avoid 5-15min charts due to noise",
      ],
      ar: [
        "عند مناطق دعم قوي (المطرقة) أو مقاومة قوية (النجم الساقط)",
        "بعد 3 شموع اتجاه على الأقل بنفس الاتجاه",
        "تجنب فريمات 5-15 دقيقة بسبب الضوضاء",
      ],
    },
    markets: {
      en: ["Forex: USD/JPY, GBP/USD majors", "Commodities: Gold & Silver at round numbers", "Stocks: Indices S&P500, NASDAQ"],
      ar: ["الفوركس: الأزواج الرئيسية USD/JPY, GBP/USD", "السلع: الذهب والفضة عند الأرقام المستديرة", "الأسهم: المؤشرات S&P500, NASDAQ"],
    },
    entry: {
      en: [
        "Hammer: small body at top, long lower wick (2x body) — bullish at support",
        "Shooting Star: small body at bottom, long upper wick — bearish at resistance",
        "Inverted Hammer: long upper wick at support = bullish",
      ],
      ar: [
        "المطرقة: جسم صغير في الأعلى، فتيل سفلي طويل (ضعف الجسم) — صاعد عند الدعم",
        "النجم الساقط: جسم صغير في الأسفل، فتيل علوي طويل — هابط عند المقاومة",
        "المطرقة المقلوبة: فتيل علوي طويل عند الدعم = صاعدة",
      ],
    },
    exit: {
      en: ["SL below hammer wick (or above shooting star wick)", "TP at nearest opposing level"],
      ar: ["وقف الخسارة أسفل فتيل المطرقة (أو فوق فتيل النجم الساقط)", "جني الأرباح عند أقرب مستوى مقابل"],
    },
    risk: { en: "Moderate", ar: "متوسطة" },
    timeframes: [
      { tf: "1H", confidence: "62%", stars: 3, rr: "1:1.5" },
      { tf: "4H", confidence: "68%", stars: 4, rr: "1:2" },
      { tf: "Daily", confidence: "70%", stars: 4, rr: "1:3" },
    ],
    pros: {
      en: ["Simple but powerful when combined with key levels", "Quick identification", "Clear stop-loss placement"],
      ar: ["بسيط وقوي عند الدمج مع المستويات الرئيسية", "تحديد سريع", "وضوح في وضع وقف الخسارة"],
    },
    cons: {
      en: ["Noisy on small timeframes", "Needs context (trend + S/R)"],
      ar: ["ضوضاء على الفريمات الصغيرة", "يحتاج سياق (اتجاه + دعم/مقاومة)"],
    },
  },
  {
    id: "ma-crossover",
    icon: GitBranch,
    color: "from-blue-400 to-indigo-500",
    name: { en: "Moving Average Crossovers", ar: "تقاطعات المتوسطات المتحركة" },
    summary: { en: "Trend-following EMA/MA crossover signals", ar: "إشارات تقاطع المتوسطات المتحركة لتتبع الاتجاه" },
    whenToUse: {
      en: [
        "Enter when the faster EMA crosses the slower one with momentum candles",
        "Use pullbacks to the EMA as re-entry opportunities",
        "Avoid in sideways markets — filter with ADX > 25",
      ],
      ar: [
        "ادخل عندما يتقاطع EMA السريع مع البطيء مع شموع زخم",
        "استخدم الارتدادات إلى EMA كفرص دخول جديدة",
        "تجنب الأسواق العرضية — افلتر مع ADX > 25",
      ],
    },
    markets: {
      en: ["Stocks & Indices: S&P500, NASDAQ (Golden/Death Cross watched by institutions)", "Crypto: BTC very responsive to 50/200 MA on weekly", "Forex: EUR/USD, GBP/JPY in trending sessions"],
      ar: ["الأسهم والمؤشرات: S&P500, NASDAQ (التقاطع الذهبي/المميت تراقبه المؤسسات)", "الكريبتو: BTC يستجيب بقوة لتقاطع 50/200 على الأسبوعي", "الفوركس: EUR/USD, GBP/JPY في جلسات الاتجاه"],
    },
    entry: {
      en: [
        "9/21 EMA — short-term swing entries (15min – 4H)",
        "50/200 MA — Golden Cross (bullish) / Death Cross (bearish) on Daily/Weekly",
        "20/50 EMA — medium-term swing (1H – Daily)",
      ],
      ar: [
        "9/21 EMA — صفقات سوينج قصيرة (15م – 4س)",
        "50/200 MA — التقاطع الذهبي (صاعد)/المميت (هابط) على الديلي/الأسبوعي",
        "20/50 EMA — سوينج متوسط (1س – يومي)",
      ],
    },
    exit: {
      en: ["Exit on opposite crossover", "Or trail stop below recent swing low"],
      ar: ["اخرج عند التقاطع المعاكس", "أو ضع وقف خسارة متحرك تحت آخر قاع"],
    },
    risk: { en: "Low to Moderate", ar: "منخفضة إلى متوسطة" },
    timeframes: [
      { tf: "9/21 EMA", confidence: "65%", stars: 3, rr: "15m–4H" },
      { tf: "50/200 MA", confidence: "78%", stars: 5, rr: "Daily–Weekly" },
      { tf: "20/50 EMA", confidence: "70%", stars: 4, rr: "1H–Daily" },
    ],
    pros: {
      en: ["One of the most widely used trend-following strategies", "Clear signals", "Used by institutions"],
      ar: ["من أكثر استراتيجيات تتبع الاتجاه استخداماً", "إشارات واضحة", "تستخدمها المؤسسات"],
    },
    cons: {
      en: ["Lagging indicator (late signals)", "Whipsaws in sideways markets"],
      ar: ["مؤشر متأخر (إشارات متأخرة)", "إشارات كاذبة في الأسواق العرضية"],
    },
  },
  {
    id: "rsi",
    icon: Activity,
    color: "from-purple-400 to-pink-500",
    name: { en: "RSI Overbought / Oversold", ar: "RSI ذروة الشراء/البيع" },
    summary: { en: "Momentum extremes and divergence reversals", ar: "نهايات الزخم وانعكاسات الدايفرجنس" },
    whenToUse: {
      en: [
        "Best in ranging/sideways markets for fades",
        "Use divergence in trending markets for reversals",
        "Combine with S/R for highest accuracy",
      ],
      ar: [
        "الأفضل في الأسواق العرضية للارتدادات",
        "استخدم الدايفرجنس في الأسواق ذات الاتجاه للانعكاسات",
        "ادمج مع الدعم/المقاومة لأعلى دقة",
      ],
    },
    markets: {
      en: ["Crypto: extremely effective on BTC, ETH (extreme RSI levels)", "Forex: works on majors when ranging", "Commodities: Gold RSI divergence reliable on 4H/Daily"],
      ar: ["الكريبتو: فعال جداً على BTC, ETH (مستويات متطرفة)", "الفوركس: يعمل على الرئيسية عند التذبذب", "السلع: دايفرجنس RSI للذهب موثوق على 4س/يومي"],
    },
    entry: {
      en: [
        "RSI < 30 = Oversold → look for buy signals",
        "RSI > 70 = Overbought → look for sell signals",
        "RSI Divergence: price makes new high/low but RSI doesn't — powerful signal",
        "RSI 50 cross = trend confirmation",
      ],
      ar: [
        "RSI < 30 = تشبع بيعي → ابحث عن إشارات شراء",
        "RSI > 70 = تشبع شرائي → ابحث عن إشارات بيع",
        "دايفرجنس RSI: السعر يصنع قمة/قاع جديد لكن RSI لا — إشارة قوية",
        "تقاطع RSI 50 = تأكيد الاتجاه",
      ],
    },
    exit: {
      en: ["Exit when RSI crosses back to 50", "SL beyond recent swing"],
      ar: ["اخرج عندما يعود RSI إلى 50", "وقف الخسارة وراء آخر قمة/قاع"],
    },
    risk: { en: "Moderate", ar: "متوسطة" },
    timeframes: [
      { tf: "Overbought/Oversold", confidence: "63%", stars: 3, rr: "Range" },
      { tf: "RSI Divergence", confidence: "74%", stars: 4, rr: "Reversal" },
      { tf: "RSI 50 Cross", confidence: "60%", stars: 3, rr: "Trend" },
    ],
    pros: { en: ["High-probability reversals on divergence", "Easy to read"], ar: ["انعكاسات عالية الاحتمال على الدايفرجنس", "سهل القراءة"] },
    cons: { en: ["Can stay overbought/oversold long in strong trends", "Needs confirmation"], ar: ["قد يبقى متشبعاً طويلاً في الاتجاهات القوية", "يحتاج تأكيد"] },
  },
  {
    id: "bollinger",
    icon: Waves,
    color: "from-cyan-400 to-teal-500",
    name: { en: "Bollinger Band Squeeze & Breakout", ar: "ضغط واختراق بولنجر باند" },
    summary: { en: "Volatility compression then explosive breakout", ar: "ضغط التذبذب ثم اختراق انفجاري" },
    whenToUse: {
      en: [
        "Enter breakout direction when squeeze resolves with volume",
        "Best before major news events (compression then explosion)",
        "Use 20-period, 2 standard deviation (default)",
      ],
      ar: [
        "ادخل في اتجاه الاختراق عندما ينحل الضغط مع زيادة الحجم",
        "الأفضل قبل أحداث إخبارية كبرى (انضغاط ثم انفجار)",
        "استخدم 20-فترة، 2 انحراف معياري (الافتراضي)",
      ],
    },
    markets: {
      en: ["Crypto: BTC and altcoins squeeze before massive moves", "Forex: EUR/USD before NFP/FOMC", "Stocks: individual stocks before earnings"],
      ar: ["الكريبتو: BTC والالتكوينز تنضغط قبل حركات كبيرة", "الفوركس: EUR/USD قبل NFP/FOMC", "الأسهم: الأسهم الفردية قبل الأرباح"],
    },
    entry: {
      en: [
        "Squeeze: bands narrow to tightest point — signals upcoming breakout",
        "Breakout: price closes outside band with volume = strong signal",
        "Mean Reversion: price touches upper band in downtrend = sell; lower band in uptrend = buy",
      ],
      ar: [
        "الانضغاط: تضيق الباندات إلى أضيق نقطة — تشير لاختراق قريب",
        "الاختراق: السعر يغلق خارج الباند مع حجم = إشارة قوية",
        "الارتداد للوسط: لمس الباند العلوي في اتجاه هابط = بيع؛ السفلي في اتجاه صاعد = شراء",
      ],
    },
    exit: {
      en: ["Take profit at opposite band", "SL on the other side of breakout candle"],
      ar: ["جني الأرباح عند الباند المقابل", "وقف الخسارة على الجانب الآخر من شمعة الاختراق"],
    },
    risk: { en: "Moderate to High", ar: "متوسطة إلى مرتفعة" },
    timeframes: [
      { tf: "Squeeze Breakout", confidence: "72%", stars: 4, rr: "4H–Daily" },
      { tf: "Mean Reversion", confidence: "65%", stars: 3, rr: "1H–4H" },
      { tf: "Band Walk", confidence: "70%", stars: 4, rr: "Daily–Weekly" },
    ],
    pros: { en: ["Explosive entries after compression", "Adapts to volatility"], ar: ["دخولات انفجارية بعد الانضغاط", "يتكيف مع التذبذب"] },
    cons: { en: ["False breakouts common", "Requires volume confirmation"], ar: ["اختراقات وهمية شائعة", "يحتاج تأكيد بالحجم"] },
  },
  {
    id: "macd",
    icon: BarChart3,
    color: "from-rose-400 to-red-500",
    name: { en: "MACD Crossover Strategy", ar: "استراتيجية تقاطع MACD" },
    summary: { en: "Trend + momentum confluence via MACD line and histogram", ar: "تقاطع الاتجاه والزخم عبر خط MACD والهيستوجرام" },
    whenToUse: {
      en: [
        "Use on 4H and Daily for swing trades — avoid 1min/5min",
        "Combine with EMA trend direction — only take signals in trend direction",
      ],
      ar: [
        "استخدمها على 4س واليومي لصفقات السوينج — تجنب 1د/5د",
        "ادمج مع اتجاه EMA — خذ الإشارات في اتجاه الترند فقط",
      ],
    },
    markets: { en: ["Forex majors during trending sessions", "Stocks: S&P500 MACD divergence has predicted major tops/bottoms", "Crypto: effective on weekly BTC charts"], ar: ["الفوركس الرئيسي في جلسات الاتجاه", "الأسهم: دايفرجنس MACD على S&P500 توقع قمم/قيعان كبرى", "الكريبتو: فعال على الأسبوعي للبيتكوين"] },
    entry: {
      en: [
        "Bullish: MACD line crosses above signal line — buy",
        "Bearish: MACD line crosses below signal line — sell",
        "Histogram expanding = momentum building; shrinking = fading",
        "MACD Divergence = one of the most reliable reversal signals",
      ],
      ar: [
        "صاعد: خط MACD يتقاطع فوق خط الإشارة — شراء",
        "هابط: خط MACD يتقاطع تحت خط الإشارة — بيع",
        "اتساع الهيستوجرام = زخم متصاعد؛ تقلصه = زخم متلاشي",
        "دايفرجنس MACD = من أكثر إشارات الانعكاس موثوقية",
      ],
    },
    exit: { en: ["Exit on opposite cross or histogram reversal", "SL beyond recent swing"], ar: ["اخرج عند التقاطع المعاكس أو انعكاس الهيستوجرام", "وقف الخسارة وراء آخر قمة/قاع"] },
    risk: { en: "Moderate", ar: "متوسطة" },
    timeframes: [
      { tf: "Crossover", confidence: "62%", stars: 3, rr: "4H–Daily" },
      { tf: "Divergence", confidence: "75%", stars: 4, rr: "4H–Weekly" },
      { tf: "Histogram Fade", confidence: "60%", stars: 3, rr: "Daily" },
    ],
    pros: { en: ["Combines trend + momentum", "Divergence very reliable"], ar: ["يجمع الاتجاه والزخم", "الدايفرجنس موثوق جداً"] },
    cons: { en: ["Lagging on lower timeframes", "Many false signals on small TF"], ar: ["متأخر على الفريمات الصغيرة", "إشارات كاذبة كثيرة"] },
  },
  {
    id: "sr-flip",
    icon: Layers,
    color: "from-yellow-400 to-orange-500",
    name: { en: "Support & Resistance Flips", ar: "انقلاب الدعم والمقاومة" },
    summary: { en: "Broken level becomes opposite level — retest entry", ar: "المستوى المكسور يصبح مستوى معاكس — دخول على إعادة الاختبار" },
    whenToUse: {
      en: [
        "Most powerful after consolidation breakout with volume",
        "Higher TF flips (Daily/Weekly) carry more weight",
        "Combine with candlestick confirmation at flip zone",
      ],
      ar: [
        "الأقوى بعد اختراق ضغط مع حجم",
        "انقلابات الفريمات العالية (يومي/أسبوعي) أقوى",
        "ادمج مع تأكيد شمعي عند منطقة الانقلاب",
      ],
    },
    markets: { en: ["Universal — all markets", "Especially powerful on Indices (Dow, S&P, NASDAQ) and Gold", "Forex round numbers (1.0000, 1.2000) act as major S/R"], ar: ["عام — جميع الأسواق", "قوي بشكل خاص على المؤشرات (Dow, S&P, NASDAQ) والذهب", "الأرقام المستديرة في الفوركس (1.0000, 1.2000) دعوم/مقاومات رئيسية"] },
    entry: {
      en: [
        "Wait for clean break and close ABOVE resistance (not just a wick)",
        "Wait for price to retest the broken level from above",
        "Enter on retest with tight stop below former resistance",
      ],
      ar: [
        "انتظر اختراق نظيف وإغلاق فوق المقاومة (ليس مجرد فتيل)",
        "انتظر السعر ليعيد اختبار المستوى المكسور من الأعلى",
        "ادخل عند إعادة الاختبار مع وقف ضيق تحت المقاومة السابقة",
      ],
    },
    exit: {
      en: ["1H: SL 5-10 pips below level", "4H: SL 10-20 pips below level", "Daily: close below level"],
      ar: ["1س: وقف 5-10 نقاط تحت المستوى", "4س: وقف 10-20 نقطة تحت المستوى", "اليومي: إغلاق تحت المستوى"],
    },
    risk: { en: "Low to Moderate", ar: "منخفضة إلى متوسطة" },
    timeframes: [
      { tf: "1H", confidence: "62%", stars: 3, rr: "1:1.5" },
      { tf: "4H", confidence: "73%", stars: 4, rr: "1:2" },
      { tf: "Daily", confidence: "82%", stars: 5, rr: "1:3" },
    ],
    pros: { en: ["One of the most reliable structural concepts", "Clear stop placement"], ar: ["من أكثر المفاهيم الهيكلية موثوقية", "وقف خسارة واضح"] },
    cons: { en: ["Requires patience for retest", "Can miss explosive moves"], ar: ["يحتاج صبر لإعادة الاختبار", "قد يفوّت الحركات الانفجارية"] },
  },
  {
    id: "fvg",
    icon: Crosshair,
    color: "from-violet-400 to-purple-600",
    name: { en: "Fair Value Gaps (FVG)", ar: "فجوات القيمة العادلة (FVG)" },
    summary: { en: "Smart Money Concepts — institutions fill gaps", ar: "مفاهيم سمارت موني — المؤسسات تملأ الفجوات" },
    whenToUse: {
      en: [
        "After a strong impulsive move — look for retracement into FVG",
        "Most powerful on 15min, 1H, 4H timeframes",
        "Combine with order blocks and liquidity sweeps",
      ],
      ar: [
        "بعد حركة دفع قوية — ابحث عن ارتداد إلى FVG",
        "الأقوى على فريمات 15د، 1س، 4س",
        "ادمج مع Order Blocks ومسح السيولة",
      ],
    },
    markets: { en: ["Indices: NAS100, SPX (institutions create FVGs during news)", "Forex: GBP/USD, EUR/USD — London open creates multiple FVGs", "Crypto: BTC, ETH on lower TFs"], ar: ["المؤشرات: NAS100, SPX (المؤسسات تصنع FVGs خلال الأخبار)", "الفوركس: GBP/USD, EUR/USD — افتتاح لندن يصنع FVGs متعددة", "الكريبتو: BTC, ETH على الفريمات الصغيرة"] },
    entry: {
      en: [
        "3-candle pattern: Candle 1 high and Candle 3 low don't overlap — gap = FVG",
        "Bullish FVG: gap below current price — acts as support",
        "Bearish FVG: gap above current price — acts as resistance",
        "Enter when price retraces into the FVG",
      ],
      ar: [
        "نمط 3 شموع: قمة الشمعة 1 وقاع الشمعة 3 لا يتداخلان — الفجوة = FVG",
        "FVG صاعد: الفجوة تحت السعر الحالي — تعمل كدعم",
        "FVG هابط: الفجوة فوق السعر الحالي — تعمل كمقاومة",
        "ادخل عندما يرتد السعر إلى FVG",
      ],
    },
    exit: { en: ["TP at next liquidity pool", "SL beyond FVG boundary"], ar: ["جني الأرباح عند أقرب بركة سيولة", "وقف الخسارة وراء حدود FVG"] },
    risk: { en: "Moderate", ar: "متوسطة" },
    timeframes: [
      { tf: "15min", confidence: "63%", stars: 3, rr: "1:2" },
      { tf: "1H", confidence: "71%", stars: 4, rr: "1:3" },
      { tf: "4H", confidence: "74%", stars: 4, rr: "1:4" },
    ],
    pros: { en: ["Excellent risk/reward", "Smart Money concept", "Works in trending markets"], ar: ["نسبة مخاطرة/مكافأة ممتازة", "مفهوم سمارت موني", "يعمل في الأسواق ذات الاتجاه"] },
    cons: { en: ["Requires SMC knowledge", "Not all FVGs get filled"], ar: ["يحتاج معرفة بـ SMC", "ليست كل FVGs تمتلئ"] },
  },
  {
    id: "three-soldiers",
    icon: Flag,
    color: "from-lime-400 to-green-600",
    name: { en: "Three White Soldiers & Three Black Crows", ar: "الجنود الثلاثة البيض والغربان الثلاثة السود" },
    summary: { en: "Powerful 3-candle continuation / reversal", ar: "نمط 3 شموع قوي للاستمرار/الانعكاس" },
    whenToUse: {
      en: [
        "After a major reversal or breakout of long consolidation",
        "Stronger on Daily and Weekly timeframes",
        "Confirm with rising volume on each of the three candles",
      ],
      ar: [
        "بعد انعكاس كبير أو اختراق ضغط طويل",
        "أقوى على الفريم اليومي والأسبوعي",
        "تأكد من ارتفاع الحجم على كل من الشموع الثلاث",
      ],
    },
    markets: { en: ["Stocks: very reliable after earnings beats", "Indices: S&P500, NASDAQ for macro direction", "Commodities: Oil and Gold after geopolitical events"], ar: ["الأسهم: موثوق جداً بعد تجاوز الأرباح للتوقعات", "المؤشرات: S&P500, NASDAQ للاتجاه الكلي", "السلع: النفط والذهب بعد الأحداث الجيوسياسية"] },
    entry: {
      en: [
        "Three White Soldiers: 3 consecutive large bullish candles, each closing near their high",
        "Three Black Crows: 3 consecutive large bearish candles, each closing near their low",
        "Each candle should open within the previous candle's body",
      ],
      ar: [
        "الجنود الثلاثة البيض: 3 شموع صاعدة كبيرة متتالية، كل واحدة تغلق بالقرب من قمتها",
        "الغربان الثلاثة السود: 3 شموع هابطة كبيرة متتالية، كل واحدة تغلق بالقرب من قاعها",
        "كل شمعة يجب أن تفتح داخل جسم الشمعة السابقة",
      ],
    },
    exit: { en: ["TP at next major level", "SL below first soldier (or above first crow)"], ar: ["جني الأرباح عند أقرب مستوى رئيسي", "وقف الخسارة تحت أول جندي (أو فوق أول غراب)"] },
    risk: { en: "Moderate to High", ar: "متوسطة إلى مرتفعة" },
    timeframes: [
      { tf: "Three White Soldiers", confidence: "71%", stars: 4, rr: "Daily–Weekly" },
      { tf: "Three Black Crows", confidence: "69%", stars: 4, rr: "Daily–Weekly" },
    ],
    pros: { en: ["Shows strong sustained momentum", "Highly reliable after prolonged trend"], ar: ["يظهر زخماً قوياً ومستداماً", "موثوق جداً بعد اتجاه ممتد"] },
    cons: { en: ["Late entry (3 candles in)", "Risk of pullback after"], ar: ["دخول متأخر (بعد 3 شموع)", "خطر ارتداد بعدها"] },
  },
  {
    id: "war-crisis",
    icon: AlertTriangle,
    color: "from-red-500 to-orange-600",
    name: { en: "Trading During War & Geopolitical Crisis", ar: "التداول خلال الحروب والأزمات الجيوسياسية" },
    summary: { en: "Safe-haven flows + 7-step crisis playbook", ar: "تدفقات الملاذات الآمنة + خطة من 7 خطوات للأزمات" },
    whenToUse: {
      en: [
        "When war news breaks — DO NOT trade immediately (spreads widen, slippage extreme)",
        "Wait 15-30 minutes for initial panic to settle",
        "Identify clear direction (Gold up? Oil up? Stocks down?)",
        "Enter on FIRST pullback — not the initial spike",
        "Use wider stops (2-3x normal)",
        "Take partial profits quickly (30-50%)",
        "Reduce overall position size by 50%",
      ],
      ar: [
        "عند صدور أخبار الحرب — لا تتداول مباشرة (السبريد يتسع والانزلاق متطرف)",
        "انتظر 15-30 دقيقة حتى يهدأ الذعر الأولي",
        "حدد الاتجاه الواضح (هل الذهب يرتفع؟ النفط؟ الأسهم تنزل؟)",
        "ادخل على أول ارتداد — وليس الاندفاعة الأولى",
        "استخدم وقف خسارة أوسع (2-3 أضعاف العادي)",
        "خذ أرباحاً جزئية بسرعة (30-50%)",
        "قلل حجم المركز الإجمالي بنسبة 50%",
      ],
    },
    markets: {
      en: [
        "BUY (Safe Havens): Gold, USD, Swiss Franc (CHF), US Treasuries, Silver",
        "SELL/AVOID: Stocks of affected region, EM currencies (TRY, ZAR, RUB), Crypto short-term",
        "Oil-exporting currencies strengthen: CAD, NOK, RUB (if no sanctions)",
      ],
      ar: [
        "شراء (الملاذات الآمنة): الذهب، الدولار، الفرنك السويسري CHF، السندات الأمريكية، الفضة",
        "بيع/تجنب: أسهم المنطقة المتأثرة، عملات الأسواق الناشئة (TRY, ZAR, RUB)، الكريبتو على المدى القصير",
        "عملات الدول المصدرة للنفط تقوى: CAD, NOK, RUB (إذا لم تفرض عقوبات)",
      ],
    },
    entry: {
      en: [
        "Middle East war → Buy Gold, Oil, USD; Sell Stocks, EM",
        "Russia/Europe → Buy Gold, CHF, Natural Gas; Sell EUR, East EU stocks",
        "US/China tension → Buy Gold, JPY, USD; Sell Tech, CNY, AUD",
        "Nuclear threat → Buy Gold, CHF, Cash; Avoid everything else",
        "Cyber war → Buy Crypto (BTC), Gold; Avoid Banks, Tech infra",
      ],
      ar: [
        "حرب الشرق الأوسط ← شراء الذهب والنفط والدولار؛ بيع الأسهم والأسواق الناشئة",
        "روسيا/أوروبا ← شراء الذهب والفرنك والغاز الطبيعي؛ بيع اليورو وأسهم شرق أوروبا",
        "توتر أمريكي-صيني ← شراء الذهب والين والدولار؛ بيع التكنولوجيا واليوان والدولار الأسترالي",
        "تهديد نووي ← شراء الذهب والفرنك والكاش؛ تجنب كل شيء آخر",
        "حرب إلكترونية ← شراء الكريبتو (BTC) والذهب؛ تجنب البنوك والبنية التحتية التقنية",
      ],
    },
    exit: {
      en: [
        "Never risk more than 1% per trade",
        "Keep 30-40% of capital in cash/stable assets",
        "Set wider stop losses (2-3x normal ATR)",
        "Avoid overnight positions unless analysis is clear",
      ],
      ar: [
        "لا تخاطر بأكثر من 1% لكل صفقة",
        "احتفظ بـ 30-40% من رأس المال في كاش/أصول مستقرة",
        "ضع وقف خسارة أوسع (2-3 أضعاف ATR العادي)",
        "تجنب الصفقات بين عشية وضحاها إلا إذا كان التحليل واضحاً",
      ],
    },
    risk: { en: "High (mitigated by smaller size + wider stops)", ar: "مرتفعة (مخففة بحجم أصغر ووقف خسارة أوسع)" },
    timeframes: [
      { tf: "Initial reaction", confidence: "—", stars: 5, rr: "Wait 15-30m" },
      { tf: "First pullback", confidence: "High", stars: 5, rr: "Enter here" },
      { tf: "Continuation", confidence: "Mod", stars: 3, rr: "Trail SL" },
    ],
    pros: { en: ["Most volatile (hence profitable) market conditions", "Clear directional flows"], ar: ["أكثر ظروف السوق تقلباً (وبالتالي ربحية)", "تدفقات اتجاهية واضحة"] },
    cons: { en: ["Extreme volatility & spreads", "Rapid reversals", "High emotional stress"], ar: ["تقلبات وسبريد متطرف", "انعكاسات سريعة", "ضغط نفسي عالي"] },
  },
];
